import { Component, OnInit, Input, ViewChild, SimpleChanges, forwardRef } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { 
    IonContent, IonModal, IonHeader, IonFooter, IonToolbar, IonGrid, IonRow, IonCol, IonIcon, IonSelect, IonSelectOption, IonLabel, IonSpinner
} from '@ionic/angular/standalone';
import { ReactiveFormsModule, ControlContainer, FormGroupDirective, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { MarvModalService } from '@marv/services';
import { MarvListComponent, MarvButtonComponent, MarvListItem, MarvListExternalSource, MarvTagComponent, MarvSpeechToTextComponent, MarvMessageComponent } from '@marv/components';
import { ApiService } from '@app/services';

export class MarvSelectItem extends MarvListItem {}
export class MarvSelectExternalSource extends MarvListExternalSource {}

@Component({
    selector: 'marv-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    imports: [
        IonContent, IonModal, IonHeader, IonFooter, IonToolbar, IonGrid, IonRow, IonCol, IonIcon, IonSelect, IonSelectOption, IonLabel, IonSpinner,
        ReactiveFormsModule, TranslatePipe, AsyncPipe, 
        forwardRef(() => MarvListComponent), forwardRef(() => MarvButtonComponent), forwardRef(() => MarvTagComponent), forwardRef(() => MarvSpeechToTextComponent), 
        forwardRef(() => MarvMessageComponent)
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ]
})
export class MarvSelectComponent  implements OnInit {

    @Input({alias : 'label', required : false}) labelSelect: string = '';
    @Input({alias : 'messageInfo', required : false}) messageInfo: string = '';
    @Input({alias : 'icon', required : false}) iconSelect: string = '';
    @Input({alias : 'formName', required : false}) formName: string|number = '';
    @Input({alias : 'errorText', required : false}) errorText: string = this.translateService.instant('MSG_ERROR_SELECT_MANDATORY');
    @Input({alias : 'disabled', required : false}) disabled: boolean = false;
    @Input({alias : 'items', required : false}) items: Array<MarvSelectItem>;
    @Input({alias : 'externalSource', required : false}) externalSource: MarvSelectExternalSource|undefined;
    @Input({alias : 'maxItemsDisplayed', required : false}) maxItemsDisplayed: number = 100;
    @Input({alias : 'itemsSelected', required : false}) itemsSelected: Map<number|string|boolean, MarvSelectItem> = new Map();
    @Input({alias : 'multiple', required : false}) multiple: boolean = false; 
    @Input({alias : 'displayItemsColors', required : false}) displayItemsColors: boolean = false;
    @Input({alias : 'displayItemsSelected', required : false}) displayItemsSelected: boolean = false;
    @Input({alias : 'autoClose', required : false}) autoClose: boolean = true;
    @Input({alias : 'noMargin', required : false}) noMargin: boolean = false;
    @Input({alias : 'withSpeechToText', required : false}) withSpeechToText: boolean = false;
    @Input({alias : 'withSearch', required : false}) withSearch: boolean = true;

    @ViewChild('modalList') modalList: IonModal;
    @ViewChild('marvList') marvList: MarvListComponent;
    @ViewChild('marvSpeechToText') marvSpeechToText: MarvSpeechToTextComponent;
    
    itemsSelectedCopy: Map<number|string|boolean, MarvSelectItem> = new Map();
    initCount: boolean = false;
    isSearchingText: boolean = false;
    searchDefault: string = '';
    hideListModal: boolean = false;
    isRequired: boolean = false;
    
    updateStatusChange: Subscription;
       
    constructor(
        public translateService: TranslateService,
        public formGroupDirective: FormGroupDirective,
        private marvModalService: MarvModalService,
        private apiService: ApiService
    ) { }

    async ngOnInit() {        
        this.isRequired = this.formGroupDirective.form.controls[this.formName].hasValidator(Validators.required); 
        this.updateStatusChange = this.formGroupDirective.form.controls[this.formName].statusChanges.subscribe((s) => {
            this.isRequired = this.formGroupDirective.form.controls[this.formName].hasValidator(Validators.required);
        });             
    }
    
    async ngOnChanges(changes: SimpleChanges) {  
    
        if(changes['itemsSelected'] && changes['itemsSelected'].currentValue){
            this.itemsSelectedCopy = new Map(this.itemsSelected);
        }
                  
        if(changes['disabled'] && changes['disabled'].currentValue !== null){    
            if(this.disabled === true){
                this.formGroupDirective.form.controls[this.formName].disable({emitEvent: false});
            }
            else{
                this.formGroupDirective.form.controls[this.formName].enable({emitEvent: false});
            }
        }
    }
    
    ngOnDestroy() {           
        if(this.updateStatusChange){
            this.updateStatusChange.unsubscribe();
        }
    }

    compareWith(currentValue: any, compareValue: any) : boolean {
        return currentValue == compareValue;
    }

    openListModal(){     
        if(!this.disabled){
            if(!this.externalSource){      

                this.itemsSelectedCopy.clear();

                let values = this.formGroupDirective.form.controls[this.formName].value;
                if(!Array.isArray(values)){
                   values = [values]; 
                }
                values.forEach((value: string|number) => {
                    let item = this.items.find(i => i.value === value);
                    if(item){
                        this.itemsSelectedCopy.set(item.value, item);
                    }
                });
            }       
             
            this.marvModalService.open(this.modalList);
             
        }
    } 
    
    async canDismissListModal(data?: any, role?: string) {
        return role !== 'gesture';
    }
    
    async onDidDismissListModal($event: any){  
        this.marvModalService.onDidDismiss($event);
    }

    async cancelChoices() {
        this.initCount = false;
        await this.modalList.dismiss();
    }
    
    getItemsSelected(): Array<MarvSelectItem>{
        let itemsSelected: Array<MarvSelectItem> = [];        
        let values = this.formGroupDirective.form.controls[this.formName].value;
        if(!Array.isArray(values)){
           values = [values]; 
        }
        if(this.externalSource){   
            if(values.length === 0){
                this.itemsSelectedCopy.clear();
            }     
            
            this.itemsSelectedCopy.forEach(i => {
                itemsSelected.push(i);
            });
        }
        else{            
            itemsSelected = this.items.filter(i => values.indexOf(i.value) !== -1);
        }
        return itemsSelected;
    }
    
    setItemsSelected(itemsSelected: Map<number|string|boolean, MarvSelectItem>){
        let values:any = null;
        if(this.formGroupDirective.form.controls[this.formName]){            
            this.itemsSelectedCopy = itemsSelected;
            values = [];           
            itemsSelected.forEach((item) => {
                values.push(item.value);                
            });
            if(!this.multiple){            
                values = (values.length > 0 ? values[0] : null);   
            }  
            this.formGroupDirective.form.controls[this.formName].setValue(values);
            this.formGroupDirective.form.controls[this.formName].markAsTouched();
        }
    }

    validateChoices() {
        let itemsSelected = this.marvList.getItemsSelected();
        this.setItemsSelected(itemsSelected);
        this.initCount = false;
        this.modalList.dismiss();
    }
        
    unselectItem(item: MarvSelectItem){ 
        if(!this.disabled){     
            let values = this.formGroupDirective.form.controls[this.formName].value;
            if(!this.multiple){        
                values = null;
            }
            else{
                let indexValue = values.indexOf(item.value);
                if(indexValue !== -1){
                    values.splice(indexValue, 1);
                }
            }
            this.formGroupDirective.form.controls[this.formName].setValue(values); 

            if(this.externalSource){
                this.itemsSelectedCopy.delete(item.value);
            }
        }
    }
    
    getCountItemsSelected(): number{               
        let itemsSelected = this.marvList?.getItemsSelected();    
        let countItems: number = itemsSelected?.size || 0;  
        
        if(!this.displayItemsSelected && this.multiple && itemsSelected){  
            let values = this.formGroupDirective.form.controls[this.formName].value;
            if(!Array.isArray(values)){
               values = [values]; 
            }
            if(itemsSelected){
                let cpt = 0;            
                itemsSelected.forEach(i => {
                    if(values.indexOf(i.value) === -1){
                        cpt++;
                    }
                });            
                countItems = cpt;
            }
        }   
        
        return countItems;
    }
    
    onSelectItem(items: Map<number|string|boolean, MarvListItem>) {
        this.initCount = true;
        if(!this.multiple && this.autoClose && items.size > 0){
            this.validateChoices();
        }
    }    
    
    speechToText($event: any){
        $event.stopPropagation();  
        if(!this.disabled){
            this.marvSpeechToText.open();
        }
    }
    
    onDismissSpeechToText(text: string){
        if(text !== ''){
            this.isSearchingText = true;
            this.hideListModal = true;
            this.searchDefault = text;      
            this.openListModal();      
        }
    }
    
    async onSearchEnd(itemsFounded: Array<MarvListItem>){ 
        if(itemsFounded.length > 0){            
            let items = (itemsFounded.length === 1 ? itemsFounded : itemsFounded.filter(i => i.label.trim().match(new RegExp('^'+this.searchDefault+'$','gi'))));
            if(items.length === 1){                
                let itemsSelected = new Map(this.itemsSelectedCopy);
                itemsSelected.set(items[0].value, items[0]);
                this.setItemsSelected(itemsSelected);
                await this.cancelChoices();
                this.searchDefault = '';
            }            
        } 
        this.isSearchingText = false;            
        this.hideListModal = false; 
    }
}
